import { createFileRoute } from '@tanstack/react-router'
import * as React from 'react'

export const Route = createFileRoute('/publish/$id')({
  component: PublishPage,
  validateSearch: (search: Record<string, unknown>) => ({
    type: (search.type as string) || undefined,
  }),
})

type PublishState = {
  confirmed: boolean
  title: string
  visibility: 'public' | 'private'
}
function PublishPage() {
  const { id } = Route.useParams()
  const search = Route.useSearch() as { type?: string }
  const publishType = search.type === 'source-import' ? 'source-import' : 'remix-plan'
  const [publishing, setPublishing] = React.useState(false)
  const [published, setPublished] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [showDrawer, setShowDrawer] = React.useState(false)
  const [form, setForm] = React.useState<PublishState>({
    confirmed: false,
    title: '',
    visibility: 'public',
  })

  async function handlePublish() {
    if (!form.confirmed) return

    setPublishing(true)
    setError(null)

    try {
      const endpoint =
        publishType === 'source-import'
          ? `/api/v1/source-imports/${id}/try-on/publish`
          : `/api/v1/remixes/${id}/publish`
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          confirmed: true,
          title: form.title || undefined,
          visibility: form.visibility,
        }),
      })

      if (!res.ok) {
        const body = await res.json()
        throw new Error(body.error || 'Failed to publish')
      }

      setPublished(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setPublishing(false)
      setShowDrawer(false)
    }
  }

  if (published) {
    return (
      <main className="ootopia-pwa-page ootopia-pwa-confirm ootopia-pwa-surface">
        <div className="rounded-full bg-success/10 p-3">
          <svg
            className="h-6 w-6 text-success"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <title>Published successfully</title>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold ootopia-editorial-heading">Published!</h1>
        <p className="text-sm text-muted-foreground text-center">
          {publishType === 'source-import'
            ? 'Your try-on look is now live and visible to the community.'
            : 'Your remix is now live and visible to the community.'}
        </p>
        <button
          type="button"
          onClick={() => {
            window.location.href = '/looks'
          }}
          className="w-full max-w-xs rounded-xl ootopia-gradient-accent py-3 font-medium ootopia-tap-target"
        >
          Explore looks
        </button>
      </main>
    )
  }
  return (
    <div className="ootopia-pwa-page ootopia-pwa-stack">
      <div>
        <p className="ootopia-section-kicker">Public beauty board</p>
        <h1 className="mt-2 text-4xl font-black tracking-[-0.06em] ootopia-editorial-heading">
          Share the look you made yours
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {publishType === 'source-import'
            ? 'Share your try-on preview with the community.'
            : 'Share your adapted makeup recipe with the community.'}
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-danger/10 p-4">
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      {/* Title */}
      <div className="ootopia-pwa-surface p-5">
        <label htmlFor="publish-title" className="block text-sm font-medium mb-2">
          Title (optional)
        </label>
        <input
          id="publish-title"
          type="text"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Give your remix a name..."
          className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
          maxLength={100}
        />
      </div>

      {/* Visibility */}
      <div className="ootopia-pwa-surface p-5">
        <span className="block text-sm font-medium mb-3">Visibility</span>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setForm({ ...form, visibility: 'public' })}
            className={`flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
              form.visibility === 'public'
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-foreground/12 text-foreground/68 hover:border-primary/50'
            }`}
          >
            Public
          </button>
          <button
            type="button"
            onClick={() => setForm({ ...form, visibility: 'private' })}
            className={`flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
              form.visibility === 'private'
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-muted text-muted hover:border-primary/50'
            }`}
          >
            Private
          </button>
        </div>
        <p className="mt-2 text-xs text-foreground/60">
          {form.visibility === 'public'
            ? 'Visible to everyone in the community'
            : 'Only visible to you'}
        </p>
      </div>

      {/* Disclosure */}
      <div className="rounded-[1.35rem] border border-foreground/10 bg-white/55 p-4 shadow-sm">
        <p className="text-xs leading-relaxed text-foreground/68">
          <strong>What gets shared:</strong> Your adapted makeup recipe, product choices, and the
          generated preview image. Your original selfie, skin analysis data, and private information
          are never shared publicly.
        </p>
      </div>

      {/* Publish button */}
      <button
        type="button"
        onClick={() => setShowDrawer(true)}
        className="ootopia-cta w-full justify-center"
      >
        Publish Remix
      </button>

      {/* Confirmation drawer */}
      {showDrawer && (
        <div className="ootopia-pwa-modal">
          <div className="ootopia-pwa-modal__sheet">
            <h2 className="text-lg font-bold mb-2">Confirm Publish</h2>
            <p className="mb-4 text-sm text-foreground/68">
              Are you sure you want to publish this remix? Once published, it will be visible to the
              community.
            </p>

            <label className="flex items-center gap-3 mb-6">
              <input
                type="checkbox"
                checked={form.confirmed}
                onChange={(e) => setForm({ ...form, confirmed: e.target.checked })}
                className="h-4 w-4 rounded border-muted"
              />
              <span className="text-sm">I confirm this remix is ready to share</span>
            </label>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowDrawer(false)}
                className="flex-1 rounded-xl border py-3 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePublish}
                disabled={!form.confirmed || publishing}
                className="flex-1 rounded-xl bg-primary py-3 text-primary-foreground font-medium disabled:opacity-50"
              >
                {publishing ? 'Publishing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
